import React, { useMemo } from "react";
import { Alert, Box } from "@mui/material";
import NestedTaskList from "./NestedTaskList";
import KanbanView from "./KanbanView";
import TableView from "./TableView";
import TaskViewControls from "./TaskViewControls";
import { sortProjectTree } from "../../utils/taskSort";

/**
 * Renders the shared TaskManagement view region: the right-aligned controls row
 * (with an optional page-specific `leftExtras` slot) plus the active view
 * (List / Table / Kanban). The owning page holds the persisted view mode, sort,
 * and column state so it can also drive its data fetch (e.g. the `table` param).
 *
 * @param {Object} props
 * @param {Array} props.projects - Nested projects → tracks → tasks
 * @param {function} props.fetchProjects
 * @param {function} props.setProjects
 * @param {string} props.pageId
 * @param {Object} [props.listViewRef] - Ref forwarded to the List view
 * @param {string} props.viewMode
 * @param {function} props.setViewMode
 * @param {Array} props.sortCriteria
 * @param {function} props.setSortCriteria
 * @param {Object} props.visibleCols
 * @param {function} props.setVisibleCols
 * @param {React.ReactNode} [props.leftExtras] - Page-specific toolbar controls
 * @param {string} [props.emptyText]
 * @returns {React.ReactElement}
 */
const TaskViews = ({
  projects,
  fetchProjects,
  setProjects,
  pageId,
  listViewRef,
  viewMode,
  setViewMode,
  sortCriteria,
  setSortCriteria,
  visibleCols,
  setVisibleCols,
  leftExtras = null,
  emptyText = "No Task Available",
  availableViews = ["list", "table", "kanban"],
}) => {
  const toggleColumn = (key) =>
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));

  // List view sorts tasks within each track; memoised so the big list isn't
  // handed a fresh array identity on every render.
  const listProjects = useMemo(
    () => sortProjectTree(projects, sortCriteria),
    [projects, sortCriteria],
  );

  const isEmpty = !Array.isArray(projects) || projects.length === 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        {leftExtras}
        <TaskViewControls
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          sortCriteria={sortCriteria}
          onSortChange={setSortCriteria}
          visibleCols={visibleCols}
          onToggleColumn={toggleColumn}
          availableViews={availableViews}
        />
      </Box>

      {isEmpty ? (
        <Alert severity="info">{emptyText}</Alert>
      ) : viewMode === "list" ? (
        <NestedTaskList
          projects={listProjects}
          fetchProjects={fetchProjects}
          setProjects={setProjects}
          ref={listViewRef}
          pageId={pageId}
          defaultExpanded={false}
        />
      ) : viewMode === "table" ? (
        <TableView
          projects={projects}
          fetchProjects={fetchProjects}
          setProjects={setProjects}
          pageId={pageId}
          visibleCols={visibleCols}
          sortCriteria={sortCriteria}
        />
      ) : (
        <KanbanView
          projects={projects}
          fetchProjects={fetchProjects}
          setProjects={setProjects}
          sortCriteria={sortCriteria}
        />
      )}
    </Box>
  );
};

export default TaskViews;
